import { Box, SearchInput, Text, useBreakpointProp } from '@betty/moonlight';
import { selectQuest } from '../../state';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, TextInput } from 'react-native';
import { useDebouncedValue } from '@betty/toolkit/hooks';
import { GamesGrid, selectAvailableGames } from '@betty/games';
import QuestButtons from '../../components/QuestItem/components/QuestButtons/QuestButtons';
import { State, useDispatch, useSelector } from '@betty/toolkit/state';
import { SelectGameModalProps } from './SelectGameModal.types';

function SelectGameModal({ route }: SelectGameModalProps) {
  const { questId, filterGames } = route.params;
  const quest = useSelector((state: State) => selectQuest(state, questId));

  const questGames = quest?.subTypeMeta.gameIds;
  const allAvailableGames = useSelector(selectAvailableGames);
  const sortedGames = useMemo(
    () => allAvailableGames.sort((a, b) => a.name.localeCompare(b.name)),
    [allAvailableGames],
  );

  const dispatch = useDispatch();
  const searchRef = useRef<TextInput>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedGame, setSelectedGame] = useState<number>();

  const debouncedSearchText = useDebouncedValue(searchText);

  const filteredGames = useMemo(() => {
    const matchingGames = sortedGames.reduce<number[]>((acc, game) => {
      const isInQuest = !filterGames || questGames?.includes(game.id);
      const matchesSearch = game.name
        .toLowerCase()
        .includes(debouncedSearchText.toLowerCase());

      if (isInQuest && matchesSearch) {
        acc.push(game.id);
      }

      return acc;
    }, []);

    return matchingGames;
  }, [sortedGames, filterGames, questGames, debouncedSearchText]);

  const handlePress = useCallback(
    (id: number) => {
      setSelectedGame(id);
      Keyboard.dismiss();
    },
    [setSelectedGame],
  );

  const numberOfColumns = useBreakpointProp({
    mobile: undefined,
    tablet: undefined,
    desktop: 4,
  });

  useEffect(() => {
    if (!filteredGames.find((id) => id === selectedGame)) {
      setSelectedGame(undefined);
    }
  }, [dispatch, selectedGame, filteredGames]);

  return (
    <>
      <Box
        alignItems="center"
        marginHorizontal="space-600"
        marginBottom="space-600"
        gap="space-300"
      >
        <Text
          variant="h5"
          color="darkSurfacePrimary"
          locNS="quests"
          locKey="selectGameTitle"
          textAlign="center"
        />
        <Text
          variant="body-m-semi"
          color="darkSurfaceMuted"
          locNS="quests"
          locKey="selectGameSubTitle"
        />
      </Box>
      <SearchInput
        ref={searchRef}
        value={searchText}
        placeholderKey="find"
        placeholderNS="games"
        onChangeText={setSearchText}
        marginHorizontal="space-600"
        marginBottom="space-400"
      />
      {filteredGames.length ? (
        <GamesGrid
          gameIds={filteredGames}
          contentContainerStyle={{
            paddingTop: 'space-400',
            paddingBottom: 'safe-area-inset-bottom',
            paddingHorizontal: 'space-600',
          }}
          imageType="square"
          showsVerticalScrollIndicator={false}
          onGamePress={handlePress}
          gameHighlightedId={selectedGame}
          numOfColumns={numberOfColumns}
        />
      ) : (
        <Text
          variant="body-m-semi"
          color="darkSurfaceMuted"
          locNS="quests"
          locKey="noResults"
          textAlign="center"
          marginVertical="space-400"
          flex={1}
        />
      )}

      <Box
        paddingHorizontal="space-600"
        paddingTop="space-400"
        paddingBottom="space-600"
        backgroundColor="accentBackground"
      >
        {quest ? (
          <QuestButtons
            quest={quest}
            isCompleted={false}
            questHasStarted={true}
            selectGameView
            selectedGame={selectedGame}
          />
        ) : null}
      </Box>
    </>
  );
}

export default memo(SelectGameModal);
